import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, combineLatest, Subscription } from 'rxjs';
import { distinctUntilKeyChanged, skip, take } from 'rxjs/operators';
import { AppConfig } from 'src/app/helpers/app.config';
import { Candidate } from 'src/app/models/candidate.model';
import { Consultant } from 'src/app/models/consultant.model';
import { Invitation } from 'src/app/models/invitation.model';
import { QuestionGroup } from 'src/app/models/jobtype.model';
import { Office } from 'src/app/models/office.model';
import { Pagination } from 'src/app/models/pagination.model';
import { Profile } from 'src/app/models/profile.model';
import { Children, Project } from 'src/app/models/project';
import { Test } from 'src/app/models/test.model';
import { fromConsultant } from 'src/app/reducers/consultant.reducer';
import { fromHistoryInvitations } from 'src/app/reducers/history-invitations.reducer';
import { fromInvitations } from 'src/app/reducers/invitations.reducer';
import { IdealProfileService } from 'src/app/services/ideal-profile.service';
import { ReportService } from 'src/app/services/report.service';
import { UtilsService } from 'src/app/services/utils.service';

@Component({
  selector: 'ct-reports-ct',
  templateUrl: './reports-ct.component.html',
  styleUrls: ['./reports-ct.component.scss']
})
export class ReportsCtComponent implements OnInit, OnDestroy {

  @ViewChild('viewSkillsModal') viewSkillsModal;
  @ViewChild('selectSkillsModal') selectSkillsModal;

  modalRef: NgbModalRef;

  reportGenerateForm: FormGroup = this.fb.group({
    reportTypeId: [2, Validators.required],
    onlyProfileSkills: [false],
    adjustment: [false],
    idealProfile: [''],
    languageId: [1, Validators.required]
  });

  historyInvitations$: Observable<Pagination<Invitation>>;
  consultant$: Observable<Consultant>;
  invitations$: Observable<Pagination<Invitation>>;

  consultantSubscription: Subscription;
  multipleSuscription: Subscription;
  currentConsultant: Consultant;

  HCC: number[];
  officeId: number = null;

  invitationId: string;
  invitationSelected: Invitation;
  candidate: Candidate;
  testId: string;
  testSelected: Test;

  clientId: number;
  projects: Project[];
  projectId: FormControl = new FormControl('');
  projectSelected: Children;

  idealProfiles: Profile[] = [];
  idealProfileSelected: Profile;

  skillsSelected: QuestionGroup[] = [];
  listSkills: QuestionGroup[] = [];
  idSkills: number[] = [];
  // Para progressBar
  increment = 0;

  constructor(
    private fb: FormBuilder,
    private store: Store,
    private route: ActivatedRoute,
    private modal: NgbModal,
    private reportService: ReportService,
    public utilsService: UtilsService,
    public idealProfileService: IdealProfileService,
    public translateService: TranslateService
  ) {
    this.testId = this.route.snapshot.paramMap.get('testId');
    this.invitationId = this.route.snapshot.paramMap.get('invitationId');
  }

  ngOnInit(): void {
    this.historyInvitations$ = this.store.pipe(select(fromHistoryInvitations.selectHistoryInvitationsState));
    this.consultant$ = this.store.pipe(select(fromConsultant.selectConsultantState));
    this.invitations$ = this.store.pipe(select(fromInvitations.selectInvitationsState));

    this.HCC = AppConfig.settings.OFFICES_WITH_PROJECTS.split(',').map(officeId => Number(officeId));

    this.multipleSuscription = combineLatest([
      this.historyInvitations$,
      this.invitations$,
      this.consultant$,
    ]).subscribe(([historyInvitations, invitations, consultant]) => {
      this.currentConsultant = consultant
      this.reportGenerateForm.controls.languageId.setValue(this.currentConsultant.client.id);

      // Guardamos los ids
      this.officeId = consultant.office.randstadOffice;
      this.clientId = consultant.client.id;

      if (this.HCC.includes(this.officeId)) {
        this.reportGenerateForm.addControl('projectId', new FormControl('', Validators.required));
        this.getprojects(consultant.projects);
      }
      // Evitamos el error cuando el informe pertenece a una prueba antigua de ADN, que no fue creada desde Candidate Testing.
      if (this.invitationId !== '0') {
        // comprobamos si viene del historico
        if (historyInvitations?.data?.find(invitation => invitation.id.toString() === this.invitationId)) {
          this.invitationSelected = historyInvitations?.data?.find(invitation => invitation.id.toString() === this.invitationId);
          this.candidate = this.invitationSelected.candidate;

          // si no viene del historico usamos la invitaciones de la busqueda de test
        } else {
          this.invitationSelected = invitations?.data?.find(invitation => invitation.id.toString() === this.invitationId);
          this.candidate = this.invitationSelected.candidate;
        }

      } else {
        // comprobamos si viene del historico
        if (historyInvitations?.data?.find(invitation => invitation.tests.find(test => test.id.toString() === this.testId))) {
          this.invitationSelected = historyInvitations?.data?.find(invitation =>
            invitation.tests.find(test => test.id.toString() === this.testId));
          this.candidate = this.invitationSelected.candidate;

          // si no viene del historico usamos la invitaciones de la busqueda de test
        } else {
          this.invitationSelected = invitations?.data?.find(invitation =>
            invitation.tests.find(test => test.id.toString() === this.testId));
          this.candidate = this.invitationSelected.candidate;
        }
      }
      // seleccionamos el test correspondiente
      this.testSelected = this.invitationSelected.tests.find(invitationTest => invitationTest.id.toString() === this.testId);

      if ([1, 2].includes(this.testSelected?.testModelId)) {
        // obtenemos los perfiles ideales
        this.getIdealProfiles(
          this.testSelected?.testModelId, consultant.office, consultant.client.id, this.invitationSelected.jobtypeId, consultant.client.id
        );
      }
    });

    this.consultantSubscription = this.consultant$.pipe(distinctUntilKeyChanged('office'), skip(1)).subscribe(consultant => {
      if (this.invitationSelected?.confidential) {
        this.utilsService.goBack();
      }
    });

  }

  public showCTReport() {
    const formValue = { ...this.reportGenerateForm.value };
    formValue.testId = this.testId;
    formValue.origin = 'ct';
    formValue.randstadCandidate = this.candidate.randstadCandidate;
    formValue.invitationId = this.invitationId;
    if (this.projectSelected !== undefined) {
      formValue.projectId = this.projectSelected.id;
    }

    this.consultantSubscription = this.consultant$.pipe(take(1)).subscribe(consultant => {
      formValue.officeId = consultant.office.id;

      this.reportService.generateReportToken(formValue).subscribe(res => {
        this.reportService.getCTWebReport(res.token, formValue.languageId);
      }, error => this.utilsService.toast('error', 'Unexpected', true, this.utilsService.getNacsData({ endpoint: 'GET' + AppConfig.settings.API + 'report/token', body: formValue, error: { errorType: error.errorType, errorCode: error.errorCode, message: error.message } })));
    });
  }

  public async showADNReport(): Promise<void> {
    if (this.reportGenerateForm.controls.adjustment.value === '') {
      this.reportGenerateForm.controls.adjustment.setValue(false);
    }
    // Limpiar la variable que guarda las competencias seleccionadas para el informe al cambiar de oficina
    if (!this.HCC.includes(this.officeId)) {
      if (this.reportGenerateForm.controls?.skills) {
        this.cleanSelectedSkills();
      }
    }
    const formValue = { ...this.reportGenerateForm.value };

    if (formValue.idealProfile && (formValue.adjustment || formValue.onlyProfileSkills)) {
      formValue.idealProfileId = formValue.idealProfile.id;
    }

    // se añade el campo de proyecto si hay uno seleccionado
    this.settingValueProject(formValue);

    formValue.testId = this.testId;
    formValue.origin = 'ct';
    formValue.randstadCandidate = this.candidate.randstadCandidate;
    formValue.invitationId = this.invitationId;
    if (this.projectSelected !== undefined) {
      formValue.projectId = this.projectSelected.id;
    }

    this.consultantSubscription = this.consultant$.pipe(take(1)).subscribe(consultant => {
      formValue.officeId = consultant.office.id;

      this.reportService.generateReportToken(formValue).subscribe(res => {
        this.reportService.getCTWebReport(res.token, formValue.languageId);
      }, error => this.utilsService.toast('error', 'Unexpected', true, this.utilsService.getNacsData({ endpoint: 'GET' + AppConfig.settings.API + 'report/token', body: formValue, error: { errorType: error.errorType, errorCode: error.errorCode, message: error.message } })));
    });
  }

  public showMDReport() {
    const formValue = { ...this.reportGenerateForm.value };

    // eliminamos propiedades para no afectar al informe MD
    delete formValue.adjustment;
    delete formValue.onlyProfileSkills;

    formValue.testId = this.testId;
    // Origen de la prueba que se usa en el token para la generacion del reporte mindset
    formValue.origin = this.invitationSelected.id === 0 ? 'adn' : 'ct';
    formValue.randstadCandidate = this.candidate.randstadCandidate;
    formValue.invitationId = this.invitationId;
    formValue.jobtypeId = this.invitationSelected.jobtype.randstadJobtype;
    // se añade el campo de proyecto si hay uno seleccionado
    this.settingValueProject(formValue);

    this.consultantSubscription = this.consultant$.pipe(take(1)).subscribe(consultant => {
      formValue.officeId = consultant.office.id;

      this.reportService.generateReportToken(formValue).subscribe(res => {
        if (this.invitationSelected.id === 0 || (this.invitationSelected.id !== 0 && res.invitations[0].tests[0].result.mindsetDigital)) {
          this.reportService.getMDWebReport(res.token, formValue.languageId);
        } else {
          this.utilsService.toast('warning', 'NoAdnResult');
        }
      }, error => this.utilsService.toast('error', 'Unexpected', true, this.utilsService.getNacsData({ endpoint: 'GET' + AppConfig.settings.API + '/report/token', body: formValue, error: { errorType: error.errorType, errorCode: error.errorCode, message: error.message } })));
    });
  }

  showViewSkillsModal() {
    if (!this.modal.hasOpenModals()) {
      this.modalRef = this.modal.open(this.viewSkillsModal, { ariaLabelledBy: 'Ver-Competencias', backdrop: 'static', keyboard: false, size: 'l' });
    }
  }

  showSelectSkillsModal() {
    if (!this.modal.hasOpenModals()) {
      // Comprobamos si existe
      this.findskillsSelected();
      this.modalRef = this.modal.open(this.selectSkillsModal, { ariaLabelledBy: 'Seleccionar-Competencias', backdrop: 'static', keyboard: false, size: 'l' });
    }
  }

  getIdealProfiles(testModelId: number, office: Office, languageId: number, jobtypeId: number, clientId?: number) {
    this.idealProfileService.getByBussines(testModelId, office.id, languageId, clientId).pipe(take(1)).subscribe(res => {
      res.map(profile => {
        if (profile.isUb) {
          profile.name = this.translateService.instant('JOBTYPES.' + (profile.jobtype?.randstadJobtype ?? '0'))
        }
      })
      if (testModelId === 1) {
        this.idealProfiles = res.filter(idealProfile => idealProfile.jobtypeId === jobtypeId || !idealProfile.jobtypeId);
      } else {
        this.idealProfiles = res;
        if (this.idealProfileSelected !== undefined) {
          this.reportGenerateForm.get('idealProfile')
            .setValue(res.find(idealProfile => idealProfile.jobtypeId === this.idealProfileSelected.jobtypeId));
        }
      }

      if (this.idealProfileSelected === undefined) {
        // con el jobtype de la invitación seleccionamos el perfil ideal que saldrá predefinido en el selector
        this.idealProfileSelected = res.find(idealProfile => idealProfile.jobtypeId === jobtypeId);
        this.reportGenerateForm.get('idealProfile').setValue(this.idealProfileSelected);
      }
      this.listSkills = [...this.idealProfiles.find(idealProfile => idealProfile.testModelId = 2)?.jobtype?.questionGroups ?? []];
    });
  }

  getprojects(projects) {
    this.projectSelected = undefined;
    this.projects = projects;
  }

  setClientAndProject(event) {
    if (event.target.value.includes(',')) {
      const projectIndex: number = event.target.value.split(',')[0];
      const childIndex: number = event.target.value.split(',')[1];
      this.projectSelected = this.projects[projectIndex].children[childIndex];
      // Obtenemos los datos de competencias personalizadas del cliente, por proyecto seleccionado. Y refrescamos los nombres de las competencias.
      this.translateService.use('projects__' + this.projectSelected.id + '__es-ES');
    } else {
      this.projectSelected = undefined;
      this.findskillsSelected();
      this.translateService.use(this.currentConsultant?.client.defaultLanguage ?? 'es-ES');
    }
  }

  // Controlamos los valores que se seleccionan
  selectSkill(value: QuestionGroup) {
    if (!this.skillsSelected.includes(value)) {
      this.skillsSelected.push(value);
      this.listSkills.forEach(x => {
        if (x.id === value.id) {
          x.checked = true;
        }
      });
    } else {
      this.listSkills.forEach(x => {
        if (x.id === value.id) {
          x.checked = false;
          this.skillsSelected.splice(this.skillsSelected.findIndex(s => s.id === value.id), 1);
        }
      });
    }
  }

  // Función para guardar en array las competencias seleccionadas en el modal
  acceptSelectedSkills() {
    let concatSkill = '';
    if (this.skillsSelected.length > 0) {
      this.skillsSelected.forEach(x => {
        concatSkill += x.id + ',';
      });
      concatSkill = concatSkill.slice(0, -1);
      this.reportGenerateForm.addControl('skills', new FormControl('', Validators.required));
      this.reportGenerateForm.controls.skills.setValue(concatSkill);
      this.utilsService.toast('success', 'SELECTEDCOMPETENCES');
      this.modalRef.close('Seleccionar-Competencias');
    }
  }

  // Creamos esta función para controlar el botón "cancelar"
  // y reiniciar las variables
  closeSelectSkillsModal() {
    this.cleanSelectedSkills();
    this.modalRef.close('Seleccionar-Competencias');
    this.reportGenerateForm.removeControl('skills');
  }

  // Función que setea el valor, en caso de tener un proyecto asociado
  settingValueProject(formValue) {
    if (this.projectSelected !== undefined || this.projectSelected !== null) {
      formValue.projectId = this.projectSelected?.id;
    }
  }

  // Controlamos el evento al cambiar de perfil
  // Si tenemos competencias guardadas, vaciamos el array y desmarcamos la selección
  changeOptionIdealProfile(event) {
    if (this.HCC.includes(this.officeId)) {
      // Comparamos los objetos, si es diferente habremos cambiado de perfil, por tanto debemos vaciar la selección de competencias
      if (this.idealProfileSelected?.id !== event?.id) {
        if (this.skillsSelected.length > 0) {
          this.cleanSelectedSkills();
        }
      }
    }
    this.idealProfileSelected = event;
  }

  // Vaciamos el array de las competencias y marcamos los checkbox a falso
  cleanSelectedSkills() {
    this.reportGenerateForm.removeControl('skills');
    // Limpiamos la selección de valores de la lista de competencias
    this.listSkills.forEach(x => { x.checked = false; });
    // Limpiamos el array de las competencias seleccionadas
    this.skillsSelected = [];
  }

  // Para guardar las competencias  que se han seleccionado tras cambiar de proyecto
  findskillsSelected() {
    this.idSkills = [];
    // Comprobamos si se ajusta al perfil ideal, si es así
    // mostramos las competencias correspondientes al perfil seleccionado
    if (this.reportGenerateForm.controls.adjustment.value === true && this.idealProfileSelected) {
      let isIdealProfile = false;
      this.idealProfileSelected?.jobtype.questionGroups.forEach(skill => {
        this.idSkills.push(skill.id);
      });
      if (this.skillsSelected.length > 0) {
        this.skillsSelected.forEach(x => {
          this.idSkills.forEach(s => {
            if (x.id === +(s)) {
              isIdealProfile = true;
            }
          });
        });
        // Buscamos si existe alguna coincicedia dentro de los id del perfil ideal seleccionado
        // si es así, nos quedamos con las competencias que son iguales, sino, vaciamos el array y ponemos quitamos los checkbox
        if (!isIdealProfile) {
          this.cleanSelectedSkills();
        } else {
          const aux: QuestionGroup[] = [];
          this.idSkills.forEach(s => {
            if (this.skillsSelected.findIndex(x => x.id === s) !== -1) {
              const idx = this.skillsSelected.findIndex(x => x.id === s);
              aux.push(this.skillsSelected[idx]);
            }
          });
          this.skillsSelected = aux;
        }
      }
    } else {
      // Queremos ver TODAS las competencias
      if (this.listSkills.length !== 0) {
        this.listSkills.forEach(skill => {
          this.idSkills.push(skill.id);
        });
      }
    }
    // comprobamos si se han seleccionado competencias para marcar los checked
    if (this.skillsSelected.length !== 0) {
      this.listSkills.forEach(x => {
        this.skillsSelected.forEach(s => {
          if (s.id === x.id) {
            x.checked = true;
          }
        });
      });
    }
  }

  changeOptionReportType(reportTypeId) {
    if (reportTypeId === '1') {
      this.reportGenerateForm.get('adjustment').setValue(false);
      this.reportGenerateForm.get('onlyProfileSkills').setValue(false);
    } else if (reportTypeId === '2') {
      this.reportGenerateForm.get('adjustment').setValue(false);
      this.reportGenerateForm.get('onlyProfileSkills').setValue(false);
    } else if (reportTypeId === '3') {
      this.reportGenerateForm.get('adjustment').setValue(false);
      this.reportGenerateForm.get('onlyProfileSkills').setValue(true);
    }
  }

  ngOnDestroy(): void {
    if (this.consultantSubscription) {
      this.consultantSubscription.unsubscribe();
    }
    if (this.multipleSuscription) {
      this.multipleSuscription.unsubscribe();
    }
    // volvemos a poner el idioma español por si se ha obtenido las traducciónes de algun liente
    this.translateService.use(this.currentConsultant?.client.defaultLanguage ?? 'es-ES');
  }
}
